import React, { useEffect, useMemo, useState } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import { type CarouselApi } from "@/components/molecules/Carousel";
import {
  CarouselSection,
  CarouselSectionArrow,
} from "@/components/organisms/CarouselSection";
import ArrowUp from "@/icons/__generated__/ArrowUp/ArrowUp16";
import { IStructuredReview } from "~/types/api-types";

import { UgcCarouselCard } from "./ugc-carousel-card";

interface IUgcCarouselProps {
  listingId: string;
  spaceType: string;
  structuredReviews: IStructuredReview[];
}

export const UgcCarousel: React.FC<IUgcCarouselProps> = ({
  listingId,
  spaceType = "",
  structuredReviews = [],
}) => {
  const [api, setApi] = useState<CarouselApi>();

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": structuredReviews.map((review) => ({
        "@type": "ImageObject",
        contentUrl: `https://res.cloudinary.com/peerspace-inc/image/upload/review/${review.external_id}.jpg`,
        description: review.description,
        author: {
          "@type": "Person",
          name: `${review.guestName.first} ${review.guestName.last}`,
        },
        datePublished: review.bookingDate.split("T")[0],
        name: review.description,
      })),
    }),
    [structuredReviews],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.analyticsIntegrations.sectionImpression(
            "listing page",
            "user generated content",
          );
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );

    const ugcSection = document.getElementById("ugc-section");
    if (ugcSection) {
      observer.observe(ugcSection);
    }
    if ("#ugc-section" === window.location.hash) {
      setTimeout(() => {
        window.location.hash = "";
        window.location.hash = "#ugc-section";
      }, 1000);
    }
    return () => {
      const ugcSection = document.getElementById("ugc-section");
      if (ugcSection) {
        observer.unobserve(ugcSection);
      }
    };
  }, []);

  const groupedReviews = useMemo(() => {
    return structuredReviews.reduce(
      (reviewsArray, currReview) => {
        const existingBookingIndex = reviewsArray.findIndex(
          (review) => review.bookingId === currReview.bookingId,
        );

        if (existingBookingIndex !== -1) {
          reviewsArray[existingBookingIndex].UGCs.push({
            imageId: currReview.external_id,
            description: currReview.description,
          });
          return reviewsArray;
        }

        return [
          ...reviewsArray,
          {
            ...currReview,
            UGCs: [
              {
                imageId: currReview.external_id,
                description: currReview.description,
              },
            ],
          },
        ];
      },
      [] as Array<
        IStructuredReview & { UGCs: { imageId: string; description: string }[] }
      >,
    );
  }, [structuredReviews]);

  const carouselHeader = (
    <div className="tw-flex tw-justify-between tw-mb-4 sm:tw-mb-8">
      <Text element="h2" typeface="headline-5-b" className="tw-mb-16">
        <FormattedMessage
          id="listing.ugc_carousel.header"
          defaultMessage="Explore what’s been created in this {spaceType}"
          values={{
            spaceType: spaceType?.replaceAll("_", " ") || (
              <FormattedMessage
                id="listing.ugc_carousel.space"
                defaultMessage="space"
              />
            ),
          }}
        />
      </Text>
      {groupedReviews.length > 1 && (
        <div className="tw-hidden md:tw-flex tw-gap-24 tw-ml-24">
          <CarouselSectionArrow
            direction="left"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "ugc-carousel",
                "control",
              );
            }}
          />
          <CarouselSectionArrow
            direction="right"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "ugc-carousel",
                "control",
              );
            }}
          />
        </div>
      )}
    </div>
  );

  const carouselSubheader = (
    <Text element="p" typeface="body-2" className="tw-mb-48">
      <FormattedMessage
        id="listing.ugc_carousel.subheader"
        defaultMessage="All content {link}"
        values={{
          link: (
            <a
              key="madeinpeerspace-link"
              className="tw-underline"
              href="https://madein.peerspace.com/"
            >
              #madeinpeerspace
            </a>
          ),
        }}
      />
    </Text>
  );

  const carouselFooter = (
    <>
      {groupedReviews.length > 1 && (
        <div className="tw-flex md:tw-hidden tw-justify-between tw-mr-16 tw-mt-24">
          <CarouselSectionArrow
            direction="left"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "ugc-carousel",
                "control",
              );
            }}
          />
          <CarouselSectionArrow
            direction="right"
            api={api}
            onClick={() => {
              window.analyticsIntegrations.listingCarouselViewed(
                listingId,
                "ugc-carousel",
                "control",
              );
            }}
          />
        </div>
      )}
      <Button
        className="tw-mt-24"
        variant="underline"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <div className="tw-flex tw-items-center">
          <Text element="p" typeface="body-2-m-underline">
            <FormattedMessage
              id="listing.ugc_carousel.see_listing_details"
              defaultMessage="See listing details"
            />
          </Text>
          <div className="tw-h-20 tw-flex tw-items-center">
            <ArrowUp className="tw-ml-8" />
          </div>
        </div>
      </Button>
    </>
  );

  return (
    <div className="tw-py-80 tw-bg-gray-100 tw-w-full" id="ugc-section">
      <script
        id="reviews-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <CarouselSection
        header={carouselHeader}
        subheader={carouselSubheader}
        footer={carouselFooter}
        setApi={setApi}
        disableSwipe
      >
        {groupedReviews?.map((review, index) => (
          <UgcCarouselCard review={review} key={index} />
        ))}
      </CarouselSection>
    </div>
  );
};
